import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../MainURL';
import {
  clearSundaySchoolSession,
  getSelectedDepartment,
  getSundaySchoolSession,
  isDepartmentLocked,
  refreshSundaySchoolPermissions,
  saveSelectedDepartment,
  staffRoleOf,
  type SundaySchoolDepartment,
} from './sundaySchoolSession';
import './SundaySchoolConsole.scss';

const NAV_ITEMS = [
  {
    group: '운영',
    links: [
      { to: '/manage-ss/dashboard', label: '📊 대시보드' },
      { to: '/manage-ss/attendance', label: '✅ 출석체크' },
      { to: '/manage-ss/members', label: '👥 교적관리' },
      { to: '/manage-ss/notices', label: '📣 알림장' },
      { to: '/manage-ss/schedule', label: '🗓️ 부서일정' },
    ],
  },
  {
    group: '계정·설정',
    links: [
      { to: '/manage-ss/staff', label: '🧑‍🏫 교사·운영자 계정' },
      { to: '/manage-ss/church', label: '⛪ 교회·반 설정' },
    ],
  },
  {
    group: '내 정보',
    links: [{ to: '/manage-ss/my', label: '👤 마이페이지' }],
  },
];

/** 부서 목록을 불러와 지금 부서가 없거나 없어진 부서면 기본 부서(내 부서, 아니면 첫 부서)로 맞춘다. */
export async function syncSelectedDepartment(): Promise<{
  departments: SundaySchoolDepartment[];
  changed: boolean;
}> {
  const session = getSundaySchoolSession();
  if (!session) return { departments: [], changed: false };
  const { data } = await axios.get(`${MainURL}/sundayschool/church/departments`, {
    params: { authChurch: session.user.authChurch },
  });
  const departments: SundaySchoolDepartment[] = data.ok ? data.data.departments : [];
  const current = getSelectedDepartment();
  const wanted = isDepartmentLocked(session)
    ? departments.find((d) => d.id === session.user.departmentId)
    : (departments.find((d) => d.id === current?.id) ?? departments[0]);
  if (wanted && (wanted.id !== current?.id || wanted.name !== current?.name)) {
    saveSelectedDepartment(wanted);
    return { departments, changed: wanted.id !== current?.id };
  }
  return { departments, changed: false };
}

// 서버의 최신 역할·담당 반(GET /sundayschool/staff/me).
async function fetchMyPermissions(params: { authChurch: string; userId: number }) {
  const { data } = await axios.get(`${MainURL}/sundayschool/staff/me`, {
    params,
  });
  return data.ok ? data.data : null;
}

// 부서를 바꾸면 페이지를 새로 불러와 모든 화면이 새 부서 기준으로 다시 조회한다.
function DepartmentPicker() {
  const session = getSundaySchoolSession();
  const [departments, setDepartments] = useState<SundaySchoolDepartment[]>([]);
  const [selected, setSelected] = useState(getSelectedDepartment());
  const [className, setClassName] = useState<string | null>(null);

  useEffect(() => {
    // 로그인 뒤 역할·담당 반이 바뀌었으면 세션을 고치고 새로 그린다(버튼 노출이 세션 기준이라).
    if (session) {
      fetchMyPermissions({
        authChurch: session.user.authChurch,
        userId: session.user.id,
      })
        .then((me) => {
          setClassName(me?.className ?? null);
          return refreshSundaySchoolPermissions(async () => me);
        })
        .then((changed) => {
          if (changed) window.location.reload();
        })
        .catch((err) => console.error('GET /sundayschool/staff/me', err));
    }
    syncSelectedDepartment()
      .then(({ departments: list, changed }) => {
        if (changed) {
          window.location.reload();
          return;
        }
        setDepartments(list);
        setSelected(getSelectedDepartment());
      })
      .catch((err) => console.error('GET /sundayschool/church/departments', err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = isDepartmentLocked(session);
  const role = staffRoleOf(session);
  return (
    <div className="side-dept">
      <div className="side-label">내 권한</div>
      <div className="side-dept-name">
        {role}
        {role === '교사' && ` · ${className ?? '맡은 반 없음'}`}
      </div>
      <div className="side-label">부서</div>
      {locked || departments.length <= 1 ? (
        <div className="side-dept-name">{selected?.name ?? '-'}</div>
      ) : (
        <select
          className="select"
          value={selected?.id ?? ''}
          onChange={(e) => {
            const next = departments.find((d) => d.id === Number(e.target.value));
            if (!next) return;
            saveSelectedDepartment(next);
            window.location.reload();
          }}
        >
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function SidebarContent() {
  const location = useLocation();
  const navigate = useNavigate();

  const onLogout = () => {
    clearSundaySchoolSession();
    navigate('/manage-ss/');
  };

  return (
    <>
      <div className="side-brand">
        <span className="mark">🛠️</span> 주일학교 관리자 콘솔
      </div>
      <DepartmentPicker />
      {NAV_ITEMS.map((section) => (
        <div key={section.group}>
          <div className="side-label">{section.group}</div>
          {section.links.map((link) => (
            <Link key={link.label} className={`side-link${location.pathname === link.to ? ' on' : ''}`} to={link.to}>
              {link.label}
            </Link>
          ))}
        </div>
      ))}
      <div className="side-label">&nbsp;</div>
      <a
        className="side-link"
        href="#!"
        onClick={(e) => {
          e.preventDefault();
          onLogout();
        }}
      >
        🚪 로그아웃
      </a>
    </>
  );
}

export default function SundaySchoolLayout({ children, footNote }: { children: ReactNode; footNote: string }) {
  const location = useLocation();
  // 좁은 화면(900px 이하)에선 사이드바를 접어 두고 상단 '메뉴' 버튼으로 펼친다.
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="sundayschool-console">
      <div className="topbar">
        <div className="side-brand">
          <span className="mark">🛠️</span> 주일학교 관리자 콘솔
        </div>
        <div className="topbar-actions">
          <span className="badge badge-coral">{staffRoleOf(getSundaySchoolSession())}</span>
          <button
            type="button"
            className="btn btn-sm topbar-menu-btn"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? '닫기' : '메뉴'}
          </button>
        </div>
      </div>

      <div className="app">
        <aside className={`sidebar${menuOpen ? ' is-open' : ''}`}>
          <SidebarContent />
        </aside>

        <main className="main">
          {children}
          <div className="foot">
            <span>{footNote}</span>
          </div>
        </main>
      </div>
    </div>
  );
}
